// POS Bridge PCs -- ADMIN only.
//
// Each store PC (Windows PC or Raspberry Pi) running the Survill POS Bridge
// 2.6+ has its own identity instead of the old company-wide tokens:
//   * Setup codes: an admin makes a one-time code for a store (works once,
//     for 24 hours); someone at the store types it into the POS Bridge.
//   * PCs: every connected PC, and "Cut off" to stop one at once.
//   * Switch-over: which stores still use the old shared tokens, the key of
//     the cloud servers, and the one switch that retires the old tokens.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, MonitorSmartphone, Repeat2 } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import Sidebar from '../Component/Sidebar';
import '../CSS/LicenseManagement.css';
import '../CSS/PosBridgePCs.css';

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined,
  { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const ago = (d) => {
  if (!d) return 'never';
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400 * 2) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
};
function errText(err, fallback) {
  const d = err?.response?.data;
  if (!d || typeof d === 'string') return fallback;
  if (d.detail) return d.detail;
  return Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : v}`).join('; ') || fallback;
}
const MODE = { standalone: 'Windows PC', client: 'Raspberry Pi', 'windows-receiver': 'Windows receiver' };
const PC_STATUS = { active: 'Active', cut_off: 'Cut off', replaced: 'Set up again' };
const CODE_STATE = { unused: 'Waiting to be used', used: 'Used', expired: 'Expired', cancelled: 'Cancelled' };
const SW_STATUS = {
  old: { label: 'Still the old way', cls: 'expired' },
  mixed: { label: 'Partly switched', cls: 'soon' },
  done: { label: 'Switched', cls: 'active' },
  none: { label: 'Nothing seen', cls: 'revoked' },
};

function CopyBox({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="pb-secret">
      <span className="lic-mono">{text}</span>
      <button className="lic-btn lic-btn-light lic-btn-small"
              onClick={() => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="lic-modal-backdrop" onClick={onClose}>
      <div className="lic-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lic-modal-header">
          <h3>{title}</h3>
          <button className="lic-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="lic-modal-body">{children}</div>
      </div>
    </div>
  );
}

// ─── Setup codes ──────────────────────────────────────────────────────────────
function SetupCodes({ stores, onChanged }) {
  const [codes, setCodes] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [made, setMade] = useState(null);

  const load = useCallback(() => {
    axiosInstance.get('/api/pos-bridge/setup-codes/').then((r) => setCodes(r.data)).catch((e) => setError(errText(e, 'Failed to load setup codes.')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!storeId) { setError('Pick the store the PC is in.'); return; }
    setBusy(true); setError('');
    try {
      const r = await axiosInstance.post('/api/pos-bridge/setup-codes/', { store_id: Number(storeId), note });
      setMade(r.data);
      setNote('');
      load(); onChanged();
    } catch (e) { setError(errText(e, 'Could not make a setup code.')); }
    setBusy(false);
  };
  const cancel = async (c) => {
    if (!window.confirm(`Cancel the setup code …${c.hint} for ${c.store_name}? It will no longer work.`)) return;
    try { await axiosInstance.post(`/api/pos-bridge/setup-codes/${c.id}/cancel/`); load(); }
    catch (e) { setError(errText(e, 'Could not cancel it.')); }
  };

  return (
    <>
      <div className="lic-card pb-make">
        <h4>New setup code</h4>
        <p className="lic-hint">One code per PC. It works once and for 24 hours. At the store, open the POS Bridge (or the
          Raspberry Pi's settings page) › <b>Connect to Survill</b> and type it in. The PC then belongs to this store.</p>
        <div className="lic-filters pb-make-row">
          <div className="lic-filter-group grow">
            <label className="lic-filter-label" htmlFor="pb-store">Store</label>
            <select id="pb-store" className="lic-select" value={storeId} onChange={(e) => setStoreId(e.target.value)}>
              <option value="">Pick a store…</option>
              {stores.filter((s) => s.is_active !== false).map((s) => (
                <option key={s.id} value={s.id}>{s.store_name}{s.store_city ? ` — ${s.store_city}` : ''} (#{s.id})</option>
              ))}
            </select>
          </div>
          <div className="lic-filter-group grow">
            <label className="lic-filter-label" htmlFor="pb-note">Note (optional)</label>
            <input id="pb-note" className="lic-input" placeholder="e.g. front counter PC, Raspberry Pi" value={note}
                   maxLength={200} onChange={(e) => setNote(e.target.value)} />
          </div>
          <button className="lic-btn lic-btn-primary" disabled={busy} onClick={create}>Create setup code</button>
        </div>
        {error && <div className="lic-error">{error}</div>}
      </div>

      <div className="lic-table-container">
        <table className="lic-table">
          <thead><tr>
            <th className="lic-th">Store</th><th className="lic-th">Code</th><th className="lic-th">Note</th>
            <th className="lic-th">Made</th><th className="lic-th">Valid until</th><th className="lic-th">State</th><th className="lic-th" />
          </tr></thead>
          <tbody>
            {codes.length === 0 && <tr><td colSpan={7} className="lic-td lic-empty">No setup codes yet.</td></tr>}
            {codes.map((c) => (
              <tr key={c.id} className={c.state === 'unused' ? '' : 'pb-dim'}>
                <td className="lic-td">{c.store_name}</td>
                <td className="lic-td lic-mono">…{c.hint}</td>
                <td className="lic-td">{c.note || '—'}</td>
                <td className="lic-td lic-nowrap">{fmtDateTime(c.created_at)}<div className="lic-sub">{c.created_by || ''}</div></td>
                <td className="lic-td lic-nowrap">{fmtDateTime(c.expires_at)}</td>
                <td className="lic-td">
                  <span className={`lic-badge ${{ unused: 'soon', used: 'active', expired: 'revoked', cancelled: 'revoked' }[c.state]}`}>{CODE_STATE[c.state]}</span>
                  {c.used_at && <div className="lic-sub">{fmtDateTime(c.used_at)} · PC #{c.device_id}</div>}
                </td>
                <td className="lic-td">{c.state === 'unused' && <button className="lic-btn lic-btn-light lic-btn-small" onClick={() => cancel(c)}>Cancel</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {made && (
        <Modal title={`Setup code for ${made.store_name}`} onClose={() => setMade(null)}>
          <CopyBox text={made.code} />
          <p className="lic-hint">Works <b>once</b>, until <b>{fmtDateTime(made.expires_at)}</b> (24 hours).
            This is the only time it is shown: give it to the person at the store now. If it gets lost, cancel it and make a new one.</p>
          <div className="lic-actions"><button className="lic-btn lic-btn-primary" onClick={() => setMade(null)}>Done</button></div>
        </Modal>
      )}
    </>
  );
}

// ─── PCs ──────────────────────────────────────────────────────────────────────
function PCs({ stores, version }) {
  const [pcs, setPcs] = useState([]);
  const [store, setStore] = useState('');
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (store) q.set('store', store);
    if (status) q.set('status', status);
    axiosInstance.get(`/api/pos-bridge/devices/?${q}`).then((r) => { setPcs(r.data); setError(''); })
      .catch((e) => setError(errText(e, 'Failed to load the PCs.'))).finally(() => setLoading(false));
  }, [store, status]);
  useEffect(() => { load(); }, [load, version]);

  const cutOff = async (pc) => {
    const reason = window.prompt(
      `Cut off ${pc.machine_name || 'this PC'} (${pc.store_name})?\n\nIt stops sending receipts and clip links within a minute, `
      + 'and can only come back with a new setup code.\n\nReason (kept in the history):', '');
    if (reason === null) return;
    try { await axiosInstance.post(`/api/pos-bridge/devices/${pc.id}/cut-off/`, { reason }); load(); }
    catch (e) { setError(errText(e, 'Could not cut it off.')); }
  };

  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return pcs.filter((p) => !s || `${p.machine_name} ${p.store_name} ${p.last_ip} ${p.cert_name}`.toLowerCase().includes(s));
  }, [pcs, search]);

  const certCell = (p) => {
    if (p.mode === 'client') return <span className="lic-sub">not needed (Pi)</span>;
    if (!p.cert_not_after) return <span className="lic-sub">none yet</span>;
    const days = Math.floor((new Date(p.cert_not_after) - Date.now()) / 86400000);
    return (<>{fmtDate(p.cert_not_after)}<div className={`lic-sub${days < 14 ? ' pb-warn-text' : ''}`}>{days} days · {p.cert_name}</div></>);
  };

  return (
    <>
      <div className="lic-filters">
        <div className="lic-filter-group">
          <label className="lic-filter-label">Store</label>
          <select className="lic-select" value={store} onChange={(e) => setStore(e.target.value)}>
            <option value="">All stores</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </select>
        </div>
        <div className="lic-filter-group">
          <label className="lic-filter-label">Status</label>
          <select className="lic-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Active</option><option value="cut_off">Cut off</option>
            <option value="replaced">Set up again</option><option value="">All</option>
          </select>
        </div>
        <div className="lic-filter-group grow">
          <label className="lic-filter-label">Search</label>
          <input className="lic-input" placeholder="PC name, store, address…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      {error && <div className="lic-error">{error}</div>}
      {loading ? <div className="lic-loading">Loading…</div> : (
        <div className="lic-table-container">
          <table className="lic-table">
            <thead><tr>
              <th className="lic-th">Store</th><th className="lic-th">PC</th><th className="lic-th">Version</th>
              <th className="lic-th">Last seen</th><th className="lic-th">Certificate</th><th className="lic-th">Status</th><th className="lic-th" />
            </tr></thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={7} className="lic-td lic-empty">No PCs here yet. Connect one with a setup code.</td></tr>}
              {shown.map((p) => (
                <tr key={p.id} className={p.status === 'active' ? '' : 'pb-dim'}>
                  <td className="lic-td">{p.store_name}</td>
                  <td className="lic-td"><b>{p.machine_name || 'PC'}</b> <span className="lic-sub">#{p.id}</span>
                    <div className="lic-sub">{MODE[p.mode] || p.mode || '—'} · connected {fmtDate(p.activated_at)}</div></td>
                  <td className="lic-td">{p.app_version || '—'}</td>
                  <td className="lic-td lic-nowrap">{ago(p.last_seen)}<div className="lic-sub">{p.last_ip || ''}</div></td>
                  <td className="lic-td lic-nowrap">{certCell(p)}</td>
                  <td className="lic-td">
                    <span className={`lic-badge ${p.status === 'active' ? 'active' : 'revoked'}`}>{PC_STATUS[p.status] || p.status}</span>
                    {p.status !== 'active' && <div className="lic-sub">{fmtDateTime(p.ended_at)}{p.ended_by ? ` by ${p.ended_by}` : ''}{p.end_reason ? ` · ${p.end_reason}` : ''}</div>}
                  </td>
                  <td className="lic-td">{p.status === 'active' && <button className="lic-btn lic-btn-danger lic-btn-small" onClick={() => cutOff(p)}>Cut off…</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="lic-hint pb-foot">A PC asks the portal for a new 15-minute pass all the time, so "Last seen" is at most a
        few minutes old for a PC that is on. A cut-off PC stops within a minute.</p>
    </>
  );
}

// ─── Switch-over ──────────────────────────────────────────────────────────────
function Switchover({ onChanged }) {
  const [st, setSt] = useState(null);
  const [error, setError] = useState('');
  const [key, setKey] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('open');

  const load = useCallback(() => {
    axiosInstance.get('/api/pos-bridge/switchover/').then((r) => { setSt(r.data); setError(''); })
      .catch((e) => setError(errText(e, 'Failed to load the switch-over.')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const makeKey = async () => {
    if (st?.service?.has_key && !window.confirm('Make a NEW cloud server key? The current key stops working at once. '
      + 'Put the new one on the cloud server right away: sudo bash setup-cert-renewal.sh new-key. '
      + 'The receipt server picks it up by itself (no restart); flagged receipts are retried meanwhile.')) return;
    try { const r = await axiosInstance.post('/api/pos-bridge/service-key/'); setKey(r.data); load(); }
    catch (e) { setError(errText(e, 'Could not make the key.')); }
  };
  const block = async (s, on) => {
    if (on && s.status !== 'done' && !window.confirm(`${s.store_name} still has PCs using the OLD way. Blocking stops their receipts `
      + 'and clip links at once. Block anyway?')) return;
    try {
      const r = await axiosInstance.post('/api/pos-bridge/switchover/store/', { store_id: s.store_id, secure_only: on });
      setSt(r.data);
    } catch (e) { setError(errText(e, 'That did not work.')); }
  };
  const flip = async (enable) => {
    setBusy(true);
    try {
      const r = await axiosInstance.post('/api/pos-bridge/switchover/', { legacy_enabled: enable, confirm: typed });
      setSt(r.data); setConfirmOpen(false); setTyped(''); onChanged();
    } catch (e) { setError(errText(e, 'That did not work.')); }
    setBusy(false);
  };

  if (!st) return error ? <div className="lic-error">{error}</div> : <div className="lic-loading">Loading…</div>;
  const rows = st.stores.filter((s) => filter === 'all' || (filter === 'open' ? (s.status === 'old' || s.status === 'mixed') : s.status === filter));
  const left = st.counts.old + st.counts.mixed;
  // What each cloud server last reported. After a retirement it is only done once both say "old way closed".
  const SERVERS = [['receipt', 'Receipt server'], ['cert', 'Certificate server']];
  const srv = (k) => (st.servers && st.servers[k]) || {};
  const confirmed = (k) => srv(k).last_seen && srv(k).old_open === false
    && (!st.legacy_changed_at || new Date(srv(k).last_seen) >= new Date(st.legacy_changed_at));
  const notConfirmed = st.legacy_enabled ? [] : SERVERS.filter(([k]) => !confirmed(k)).map(([, n]) => n);
  const serverList = (
    <dl className="lic-kv pb-servers">
      {SERVERS.map(([k, name]) => (
        <React.Fragment key={k}>
          <dt>{name}</dt>
          <dd>{!srv(k).last_seen ? 'not reported yet (old version, or no key)'
            : <>{srv(k).old_open ? 'old way open' : 'old way closed'} · reported {ago(srv(k).last_seen)}</>}</dd>
        </React.Fragment>
      ))}
    </dl>
  );

  return (
    <>
      {error && <div className="lic-error">{error}</div>}
      <div className="lic-stats">
        {[['open', 'Still using the old tokens', left, 'expired'], ['done', 'Switched', st.counts.done, 'active'],
          ['none', 'Nothing seen yet', st.counts.none, 'paused'], ['all', 'All stores', st.stores.length, '']].map(([k, label, n, cls]) => (
          <div key={k} className={`lic-stat-card ${cls}${filter === k ? ' selected' : ''}`} onClick={() => setFilter(k)} role="button" tabIndex={0}>
            <div className="lic-stat-value">{n}</div><div className="lic-stat-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="pb-cards">
        <div className="lic-card">
          <h4>Cloud server key</h4>
          <p className="lic-hint">The receipt server and the certificate server use this key to talk to the portal (instead of the
            old shared token). It is shown once; it goes on the cloud server with <span className="lic-mono">setup-cert-renewal.sh upgrade</span>.</p>
          <dl className="lic-kv">
            <dt>Key</dt><dd>{st.service.has_key ? <span className="lic-mono">…{st.service.hint}</span> : 'Not made yet'}</dd>
            <dt>Made</dt><dd>{fmtDateTime(st.service.created_at)}</dd>
            <dt>Last used</dt><dd>{st.service.last_seen ? `${ago(st.service.last_seen)} (${st.service.last_ip || ''})` : 'never'}</dd>
          </dl>
          {st.cloud_server_old_token?.recent && (
            <div className="lic-warn">The cloud server still saved transactions with the OLD token {ago(st.cloud_server_old_token.last_seen)}.
              Install the new receipt server (cloud-server.js) with this key before retiring the old tokens.</div>
          )}
          <div className="lic-actions"><button className="lic-btn lic-btn-light lic-btn-small" onClick={makeKey}>
            {st.service.has_key ? 'Make a new key…' : 'Create key'}</button></div>
        </div>

        <div className={`lic-card pb-switch ${st.legacy_enabled ? 'on' : 'off'}`}>
          <h4>Old shared tokens</h4>
          {st.legacy_enabled ? (
            <>
              <p><b>Still allowed.</b> POS Bridges older than 2.6 (and 2.6 PCs not connected yet) keep working with the shared
                portal token, the shared certificate and the open receipt port.</p>
              <p className="lic-hint">Once a store shows "Switched" you can block the old way for that store alone (table below), so its old
                tokens can't be used to send receipts in its name. Retire them for everyone once every store shows "Switched". Retiring blocks the old portal token, closes the
                open receipt port and stops handing out the shared certificate, within a minute.</p>
              {serverList}
              <button className="lic-btn lic-btn-danger" onClick={() => { setTyped(''); setConfirmOpen(true); }}>Retire the old tokens…</button>
            </>
          ) : (
            <>
              <p><b>Retired</b> {fmtDateTime(st.legacy_changed_at)}{st.legacy_changed_by ? ` by ${st.legacy_changed_by}` : ''}.
                Only connected POS Bridge PCs work now.</p>
              {serverList}
              {notConfirmed.length > 0 && (
                <div className="lic-warn">Not confirmed yet by: {notConfirmed.join(', ')}. The servers check every minute; if this stays,
                  the server is still the old version or has no cloud server key — install it (ROLLOUT.md, part B).</div>
              )}
              <p className="lic-hint">Last step on the cloud server: <span className="lic-mono">sudo bash setup-cert-renewal.sh retire-wildcard</span> (cancels
                the old *.survill.com certificate, whose key was on every store PC).</p>
              <button className="lic-btn lic-btn-light lic-btn-small" onClick={() => { setTyped(''); setConfirmOpen('allow'); }}>Allow them again (emergency)…</button>
            </>
          )}
        </div>
      </div>

      <div className="lic-table-container">
        <table className="lic-table">
          <thead><tr>
            <th className="lic-th">Store</th><th className="lic-th">Status</th><th className="lic-th">Connected PCs</th>
            <th className="lic-th">Old way seen (last {st.recent_days} days in bold)</th>
            {st.legacy_enabled && <th className="lic-th">This store only</th>}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={st.legacy_enabled ? 5 : 4} className="lic-td lic-empty">{filter === 'open' ? 'No store uses the old tokens any more.' : 'Nothing here.'}</td></tr>}
            {rows.map((s) => (
              <tr key={s.store_id} className={s.is_active ? '' : 'pb-dim'}>
                <td className="lic-td">{s.store_name} <span className="lic-sub">#{s.store_id}</span>
                  <div className="lic-sub">{s.subdomain || 'no subdomain'}{s.is_active ? '' : ' · inactive'}</div></td>
                <td className="lic-td"><span className={`lic-badge ${SW_STATUS[s.status].cls}`}>{SW_STATUS[s.status].label}</span>
                  {s.old_way_blocked && st.legacy_enabled && <div className="lic-sub">old way blocked</div>}</td>
                <td className="lic-td">{s.pcs ? `${s.pcs} · ${ago(s.pcs_last_seen)}` : '—'}</td>
                <td className="lic-td">
                  {s.old_way.length === 0 ? '—' : s.old_way.map((o) => (
                    <div key={o.kind} className={o.recent ? 'pb-old recent' : 'pb-old'}>
                      {o.label} · {ago(o.last_seen)}{o.last_ip ? ` · ${o.last_ip}` : ''}{o.matched_by_ip ? ' (by address)' : ''}
                    </div>
                  ))}
                </td>
                {st.legacy_enabled && (
                  <td className="lic-td">
                    {s.old_way_blocked
                      ? <button className="lic-btn lic-btn-light lic-btn-small" onClick={() => block(s, false)}>Allow old way again</button>
                      : <button className="lic-btn lic-btn-light lic-btn-small" disabled={!s.pcs} title={s.pcs ? '' : 'Connect a PC of this store first'}
                          onClick={() => block(s, true)}>Block old way</button>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {st.unmatched_old.length > 0 && (
        <div className="lic-card">
          <h4>Old shared certificate downloaded by an unknown store</h4>
          <p className="lic-hint">These addresses downloaded the shared certificate recently, and no store has been seen there.</p>
          {st.unmatched_old.map((u) => <div key={u.store_ref} className="pb-old recent">{u.last_ip || u.store_ref} · {ago(u.last_seen)} · {u.count}×</div>)}
        </div>
      )}

      {confirmOpen && (
        <Modal title={confirmOpen === 'allow' ? 'Allow the old tokens again?' : 'Retire the old shared tokens?'} onClose={() => setConfirmOpen(false)}>
          {confirmOpen === 'allow' ? (
            <p>Only in an emergency: the old shared token and certificate key were in every installer, so anyone who has them can
              act for any store again. Type <b>ALLOW</b> to confirm.</p>
          ) : (
            <>
              {left > 0
                ? <div className="lic-warn"><b>{left} store{left === 1 ? '' : 's'} would stop sending receipts or clip links:</b>{' '}
                    {st.stores.filter((s) => s.status === 'old' || s.status === 'mixed').map((s) => s.store_name).join(', ')}</div>
                : <p>Every store seen in the last {st.recent_days} days is switched.</p>}
              <p>Within a minute: the old portal token stops working everywhere, the open receipt port closes and the shared certificate
                is no longer handed out. Type <b>RETIRE</b> to confirm.</p>
              {SERVERS.some(([k]) => !srv(k).last_seen) && (
                <div className="lic-warn">A cloud server has not reported to the portal yet, so it would not learn about the retirement.
                  Install the new cloud servers first (ROLLOUT.md, part B).</div>
              )}
            </>
          )}
          <input className="lic-input" value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
          <div className="lic-actions" style={{ marginTop: 12 }}>
            <button className="lic-btn lic-btn-light" onClick={() => setConfirmOpen(false)}>Keep as it is</button>
            {confirmOpen === 'allow'
              ? <button className="lic-btn lic-btn-warning" disabled={busy || typed.trim().toUpperCase() !== 'ALLOW'} onClick={() => flip(true)}>Allow again</button>
              : <button className="lic-btn lic-btn-danger" disabled={busy || typed.trim().toUpperCase() !== 'RETIRE'} onClick={() => flip(false)}>Retire the old tokens</button>}
          </div>
        </Modal>
      )}
      {key && (
        <Modal title="Cloud server key" onClose={() => setKey(null)}>
          <CopyBox text={key.key} />
          <p className="lic-hint">Shown only now. On the cloud server run{' '}
            <span className="lic-mono">{key.replaced_old_key ? 'sudo bash setup-cert-renewal.sh new-key' : 'sudo bash setup-cert-renewal.sh upgrade'}</span>{' '}
            and paste it when asked.{key.replaced_old_key ? ' The old key has stopped working; the receipt server picks up the new one by itself.' : ''}</p>
          <div className="lic-actions"><button className="lic-btn lic-btn-primary" onClick={() => setKey(null)}>Done</button></div>
        </Modal>
      )}
    </>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'codes', label: 'Setup codes', Icon: KeyRound },
  { id: 'pcs', label: 'PCs', Icon: MonitorSmartphone },
  { id: 'switch', label: 'Switch-over', Icon: Repeat2 },
];

function PosBridgePCs() {
  const [tab, setTab] = useState('codes');
  const [stores, setStores] = useState([]);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    axiosInstance.get('/api/stores/').then((r) => setStores((r.data.results || r.data)
      .slice().sort((a, b) => String(a.store_name).localeCompare(String(b.store_name))))).catch(() => {});
  }, []);
  const changed = () => setVersion((v) => v + 1);

  return (
    <div className="index-box">
      <Sidebar />
      <div className="lic-container">
        <div className="lic-header">
          <div>
            <h1 className="lic-header-title">POS Bridge PCs</h1>
            <p className="lic-header-sub">Each store PC is connected once with a setup code, and can then only act for its own store. Only admins see this page.</p>
          </div>
        </div>
        <div className="lic-tabs" role="tablist" aria-label="POS Bridge PCs">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} className={`lic-tab${tab === t.id ? ' on' : ''}`} onClick={() => setTab(t.id)}>
              <t.Icon size={16} strokeWidth={1.9} /><span>{t.label}</span>
            </button>
          ))}
        </div>
        {tab === 'codes' && <SetupCodes stores={stores} onChanged={changed} />}
        {tab === 'pcs' && <PCs stores={stores} version={version} />}
        {tab === 'switch' && <Switchover onChanged={changed} />}
      </div>
    </div>
  );
}

export default PosBridgePCs;
